import React from 'react';
import './FprFormView.css';

/**
 * FprFormView — comprehensive read-only render of the FPR Update Form
 * for advisor quick-view and PDF export.
 *
 * Every field is rendered (with em-dash placeholders for blank values),
 * except child fields whose parent yes/no or life-event checkbox was
 * answered/checked "no" — those are skipped because the client wasn't
 * asked to fill them in.
 *
 * The root div has id "fpr-print-area" so the print stylesheet can
 * isolate it for PDF export.
 */

const EM_DASH = '\u2014';

const fmtCurrency = (v) => {
  if (v == null || v === '') return EM_DASH;
  const n = Number(v);
  if (!Number.isFinite(n)) return EM_DASH;
  return `$${Math.round(n).toLocaleString()}`;
};

const fmtText = (v) => (v == null || v === '' ? EM_DASH : String(v));

const fmtYesNo = (v) => {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return EM_DASH;
};

const fmtDate = (v) => fmtText(v);

const fmtBool = (v) => (v ? 'Yes' : 'No');

const titleize = (s) =>
  String(s || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

/* ── Reusable sub-components ─────────────────────────────────────── */

const Row = ({ label, value }) => (
  <div className="fpr-row">
    <div className="fpr-row-label">{label}</div>
    <div className="fpr-row-value">{value}</div>
  </div>
);

const SectionCard = ({ number, title, children }) => (
  <div className="fpr-section">
    <div className="fpr-section-header">
      <span className="fpr-section-number">{number}</span>
      <span className="fpr-section-title">{title}</span>
    </div>
    <div className="fpr-section-body">{children}</div>
  </div>
);

const SubHeading = ({ children }) => (
  <h6 className="fpr-subheading">{children}</h6>
);

const Notes = ({ data }) => {
  if (!data?.notes) return null;
  return (
    <div className="fpr-notes">
      <div className="fpr-notes-label">Notes / Comments</div>
      <div className="fpr-notes-value">{data.notes}</div>
    </div>
  );
};

const NoneOfAbove = ({ data, message = 'No changes since last review.' }) => {
  if (!data?.none_of_the_above) return null;
  return <div className="fpr-none-banner">{message}</div>;
};

/* ── Section 1 – Personal ────────────────────────────────────────── */

const SECTION_PERSONAL = (data) => {
  const d = data || {};
  const sentimentLabel = {
    green: 'Good',
    yellow: 'Mixed',
    red: 'Concerned',
  }[d.investment_sentiment] || EM_DASH;

  return (
    <>
      <SubHeading>Contact information</SubHeading>
      <Row label="First name" value={fmtText(d.first_name)} />
      <Row label="Last name" value={fmtText(d.last_name)} />
      <Row label="Phone" value={fmtText(d.phone)} />
      <Row label="Email" value={fmtText(d.email)} />
      <Row label="Reviewed contact info" value={fmtBool(d.contact_verified)} />

      <SubHeading>Household</SubHeading>
      <Row label="Shares finances with partner" value={fmtYesNo(d.has_partner)} />
      {d.has_partner === 'yes' && (
        <>
          <Row label="Partner first name" value={fmtText(d.partner_first_name)} />
          <Row label="Partner last name" value={fmtText(d.partner_last_name)} />
          <Row label="Partner phone" value={fmtText(d.partner_phone)} />
          <Row label="Partner email" value={fmtText(d.partner_email)} />
        </>
      )}

      <SubHeading>Tools &amp; Preparation</SubHeading>
      <Row label="Uses eMoney" value={fmtYesNo(d.uses_emoney)} />
      <Row
        label="Investment-check frequency"
        value={fmtText(d.investment_check_frequency && titleize(d.investment_check_frequency))}
      />
      <Row label="Feeling about investments" value={sentimentLabel} />
      <Row label="Concerns / questions for advisor" value={fmtText(d.advisor_questions)} />
      <Row label="Upcoming large expenses" value={fmtText(d.upcoming_large_expenses)} />
      <Notes data={d} />
    </>
  );
};

/* ── Section 2 – Family & Personal ───────────────────────────────── */

const FAMILY_EVENTS = [
  {
    key: 'married',
    label: 'Married',
    children: (d) => (
      <>
        <Row label="Partner name" value={fmtText(d.married_partner_name)} />
        <Row label="Partner DOB" value={fmtDate(d.married_partner_dob)} />
        <Row
          label="Updated beneficiaries / estate docs"
          value={fmtYesNo(d.married_updated_beneficiaries)}
        />
      </>
    ),
  },
  {
    key: 'divorced',
    label: 'Divorced / separated',
    children: (d) => (
      <Row
        label="Updated beneficiaries / estate docs"
        value={fmtYesNo(d.divorced_updated_beneficiaries)}
      />
    ),
  },
  {
    key: 'widowed',
    label: 'Widowed',
    children: (d) => (
      <Row
        label="Updated beneficiaries / estate docs"
        value={fmtYesNo(d.widowed_updated_beneficiaries)}
      />
    ),
  },
  {
    key: 'new_child',
    label: 'Had a baby / adopted a child',
    children: (d) => {
      const kids = (d.new_children || []).filter((c) => c && (c.name || c.dob));
      return (
        <>
          {kids.length === 0 && <Row label="Children" value={EM_DASH} />}
          {kids.map((c, i) => (
            <Row
              key={i}
              label={`Child #${i + 1}`}
              value={`${fmtText(c.name)} — DOB ${fmtDate(c.dob)}`}
            />
          ))}
          <Row
            label="Updated beneficiaries / estate docs"
            value={fmtYesNo(d.new_child_updated_beneficiaries)}
          />
        </>
      );
    },
  },
  {
    key: 'supporting_family',
    label: 'Began supporting / caring for family member',
    children: (d) => (
      <>
        <Row
          label="Estimated annual expense"
          value={fmtCurrency(d.supporting_family_annual_expense)}
        />
        <Row label="Potential duration" value={fmtText(d.supporting_family_duration)} />
        <Row label="Name & relationship" value={fmtText(d.supporting_family_relation)} />
      </>
    ),
  },
  {
    key: 'moved',
    label: 'Moved / planning to relocate',
    children: (d) => <Row label="New address" value={fmtText(d.new_address)} />,
  },
];

const SECTION_FAMILY_PERSONAL = (data) => {
  const d = data || {};
  return (
    <>
      <NoneOfAbove data={d} />
      {FAMILY_EVENTS.map((evt) => (
        <div className="fpr-event" key={evt.key}>
          <div className="fpr-event-label">
            <span className={`fpr-checkbox ${d[evt.key] ? 'is-checked' : ''}`}>
              {d[evt.key] ? '☑' : '☐'}
            </span>{' '}
            {evt.label}
          </div>
          {d[evt.key] && (
            <div className="fpr-event-children">{evt.children(d)}</div>
          )}
        </div>
      ))}
      <Notes data={d} />
    </>
  );
};

/* ── Section 3 – Career & Income ─────────────────────────────────── */

const CAREER_EVENTS = [
  {
    key: 'changed_jobs',
    label: 'Changed jobs / promotion',
    children: (d) => {
      const jobs = (d.job_changes || []).filter(
        (j) => j && (j.person || j.title || j.employer || j.employer_address || j.annual_salary || j.annual_bonus),
      );
      if (jobs.length === 0) return <Row label="Job changes" value={EM_DASH} />;
      return jobs.map((j, i) => (
        <div className="fpr-subblock" key={i}>
          <div className="fpr-subblock-title">Job change #{i + 1}</div>
          <Row label="Who" value={fmtText(j.person)} />
          <Row label="New job title" value={fmtText(j.title)} />
          <Row label="Employer" value={fmtText(j.employer)} />
          <Row label="Employer address" value={fmtText(j.employer_address)} />
          <Row label="Estimated annual salary" value={fmtCurrency(j.annual_salary)} />
          <Row label="Additional annual bonus" value={fmtText(j.annual_bonus)} />
        </div>
      ));
    },
  },
  {
    key: 'promoted',
    label: 'Change in compensation (bonus, equity, stock options)',
    children: (d) => {
      const ps = (d.promotions || []).filter(
        (p) => p && (p.person || p.details),
      );
      if (ps.length === 0) return <Row label="Compensation changes" value={EM_DASH} />;
      return ps.map((p, i) => (
        <div className="fpr-subblock" key={i}>
          <div className="fpr-subblock-title">Compensation change #{i + 1}</div>
          <Row label="Who" value={fmtText(p.person)} />
          <Row label="Details" value={fmtText(p.details)} />
        </div>
      ));
    },
  },
  {
    key: 'started_or_sold_business',
    label: 'Started or sold a business',
    children: (d) => (
      <Row label="Details" value={fmtText(d.started_or_sold_business_details)} />
    ),
  },
  {
    key: 'retired',
    label: 'Retired or planning to retire soon',
    children: (d) => <Row label="Details" value={fmtText(d.retired_details)} />,
  },
];

const SECTION_CAREER_INCOME = (data) => {
  const d = data || {};
  return (
    <>
      <NoneOfAbove data={d} />
      {CAREER_EVENTS.map((evt) => (
        <div className="fpr-event" key={evt.key}>
          <div className="fpr-event-label">
            <span className={`fpr-checkbox ${d[evt.key] ? 'is-checked' : ''}`}>
              {d[evt.key] ? '☑' : '☐'}
            </span>{' '}
            {evt.label}
          </div>
          {d[evt.key] && (
            <div className="fpr-event-children">{evt.children(d)}</div>
          )}
        </div>
      ))}
      <Notes data={d} />
    </>
  );
};

/* ── Section 4 – Financial & Planning ────────────────────────────── */

const FINANCIAL_EVENTS = [
  { key: 'new_or_consolidated_accounts', label: 'Added, consolidated, or transferred accounts', detailKey: 'new_accounts_details' },
  { key: 'new_loan', label: 'Took out a new loan (mortgage, auto, HELOC, etc.)', detailKey: 'new_loan_details' },
  { key: 'changed_investment_approach', label: 'Changed investment approach or preferences', detailKey: 'changed_investment_approach_details' },
  { key: 'interested_in_strategies', label: 'Interested in strategies (Roth conversions, charitable giving, etc.)', detailKey: 'interested_in_strategies_details' },
  { key: 'large_purchase_or_sale', label: 'Made a large purchase or sale (home, second home, major asset)', detailKey: 'large_purchase_or_sale_details' },
  { key: 'insurance_changes', label: 'Purchased or updated insurance', detailKey: 'insurance_changes_details' },
  { key: 'estate_changes', label: 'Updated estate documents or beneficiaries', detailKey: 'estate_changes_details' },
  { key: 'inheritance_or_gift', label: 'Received an inheritance or large gift', detailKey: 'inheritance_or_gift_details' },
];

const SECTION_FINANCIAL_PLANNING = (data) => {
  const d = data || {};
  return (
    <>
      <NoneOfAbove data={d} />
      {FINANCIAL_EVENTS.map((evt) => (
        <div className="fpr-event" key={evt.key}>
          <div className="fpr-event-label">
            <span className={`fpr-checkbox ${d[evt.key] ? 'is-checked' : ''}`}>
              {d[evt.key] ? '☑' : '☐'}
            </span>{' '}
            {evt.label}
          </div>
          {d[evt.key] && (
            <div className="fpr-event-children">
              <Row label="Details" value={fmtText(d[evt.detailKey])} />
            </div>
          )}
        </div>
      ))}
      <Notes data={d} />
    </>
  );
};

/* ── Section 5 – Accounts ────────────────────────────────────────── */

const hasAnyData = (obj) =>
  obj && Object.values(obj).some((v) => v !== null && v !== undefined && v !== '');

const SECTION_ACCOUNTS = (data) => {
  const d = data || {};
  const entries = (d.entries || []).filter(hasAnyData);
  return (
    <>
      {entries.length === 0 && <div className="fpr-empty">No accounts listed.</div>}
      {entries.map((e, i) => (
        <div className="fpr-subblock" key={i}>
          <div className="fpr-subblock-title">Account #{i + 1}</div>
          <Row label="Type" value={fmtText(e.type)} />
          <Row label="Institution" value={fmtText(e.institution)} />
          <Row label="Current / average value" value={fmtCurrency(e.value)} />
          <Row
            label="Interest rate (%)"
            value={e.interest_rate == null || e.interest_rate === '' ? EM_DASH : `${e.interest_rate}%`}
          />
          {e.type === 'CD' && <Row label="Maturity date" value={fmtDate(e.maturity_date)} />}
        </div>
      ))}
      <Notes data={d} />
    </>
  );
};

/* ── Section 6 – Income ──────────────────────────────────────────── */

const SECTION_INCOME = (data) => {
  const d = data || {};
  const sources = (d.sources || []).filter(
    (s) => s && (s.type || s.description || s.amount),
  );
  const total = sources.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  return (
    <>
      {sources.length === 0 && <div className="fpr-empty">No income sources listed.</div>}
      {sources.map((s, i) => (
        <div className="fpr-subblock" key={i}>
          <div className="fpr-subblock-title">Source #{i + 1}</div>
          <Row label="Type" value={fmtText(s.type)} />
          <Row label="Description" value={fmtText(s.description)} />
          <Row label="Amount (annualised)" value={fmtCurrency(s.amount)} />
        </div>
      ))}
      {total > 0 && (
        <div className="fpr-total">
          <span>Total annualised income</span>
          <span>${Math.round(total).toLocaleString()}</span>
        </div>
      )}
      <Notes data={d} />
    </>
  );
};

/* ── Section 7 – Debts ───────────────────────────────────────────── */

const SECTION_DEBTS = (data) => {
  const d = data || {};
  const entries = (d.entries || []).filter(hasAnyData);
  return (
    <>
      {entries.length === 0 && <div className="fpr-empty">No debts listed.</div>}
      {entries.map((e, i) => (
        <div className="fpr-subblock" key={i}>
          <div className="fpr-subblock-title">Debt #{i + 1}</div>
          <Row label="Type" value={fmtText(e.type)} />
          <Row label="Institution" value={fmtText(e.institution)} />
          <Row label="Principal balance" value={fmtCurrency(e.principal_balance)} />
          <Row label="Monthly payment" value={fmtCurrency(e.monthly_payment)} />
          <Row
            label="Interest rate (%)"
            value={e.interest_rate == null || e.interest_rate === '' ? EM_DASH : `${e.interest_rate}%`}
          />
          <Row label="Remaining term" value={fmtText(e.remaining_term)} />
          <Row label="New since last met?" value={fmtText(e.is_new && titleize(e.is_new))} />
        </div>
      ))}
      <Notes data={d} />
    </>
  );
};

/* ── Section 8 – Expenses ────────────────────────────────────────── */

const SECTION_EXPENSES = (data) => {
  const d = data || {};
  const expenses = (d.expenses || []).filter(
    (e) => e && (e.category || e.custom_label || e.amount),
  );
  return (
    <>
      {expenses.length === 0 && <div className="fpr-empty">No expenses listed.</div>}
      {expenses.map((e, i) => {
        const label = e.category === 'Other' && e.custom_label
          ? `Other — ${e.custom_label}`
          : (e.category || EM_DASH);
        return (
          <div className="fpr-subblock" key={i}>
            <div className="fpr-subblock-title">Expense #{i + 1}</div>
            <Row label="Category" value={label} />
            <Row label="Amount (annualised)" value={fmtCurrency(e.amount)} />
          </div>
        );
      })}
      <Notes data={d} />
    </>
  );
};

/* ── Section 9 – Documents ───────────────────────────────────────── */

const SECTION_DOCUMENTS = (data) => {
  const d = data || {};
  const files = Array.isArray(d.uploaded_files) ? d.uploaded_files : [];
  return (
    <>
      {files.length === 0 ? (
        <div className="fpr-empty">No documents uploaded.</div>
      ) : (
        files.map((f, i) => (
          <Row key={i} label={`File #${i + 1}`} value={fmtText(f.name || f.filename || f)} />
        ))
      )}
      <Notes data={d} />
    </>
  );
};

/* ── Sections registry ───────────────────────────────────────────── */

const SECTIONS = [
  { key: 'personal',           number: 1,  title: 'Personal Information',                  render: SECTION_PERSONAL },
  { key: 'family_personal',    number: 2,  title: 'Situation Updates — Family & Personal', render: SECTION_FAMILY_PERSONAL },
  { key: 'career_income',      number: 3,  title: 'Situation Updates — Career & Income',   render: SECTION_CAREER_INCOME },
  { key: 'financial_planning', number: 4,  title: 'Situation Updates — Financial & Planning', render: SECTION_FINANCIAL_PLANNING },
  { key: 'accounts',           number: 5,  title: 'Cash & Savings Accounts',                render: SECTION_ACCOUNTS },
  { key: 'income',             number: 6,  title: 'Income & Recurring Sources',             render: SECTION_INCOME },
  { key: 'debts',              number: 7,  title: 'Debts & Loans',                          render: SECTION_DEBTS },
  { key: 'expenses',           number: 8,  title: 'Expenses',                               render: SECTION_EXPENSES },
  { key: 'documents',          number: 9,  title: 'Documents',                              render: SECTION_DOCUMENTS },
];

const FprFormView = ({ formData, clientName, clientEmail }) => {
  const data = formData || {};
  return (
    <div id="fpr-print-area" className="fpr-form-view">
      <div className="fpr-print-header">
        <h4 className="mb-1">FPR Update Form</h4>
        {(clientName || clientEmail) && (
          <div className="text-muted small">
            {clientName}{clientName && clientEmail ? ' — ' : ''}{clientEmail}
          </div>
        )}
        <div className="text-muted small">
          Generated {new Date().toLocaleString()}
        </div>
      </div>
      {SECTIONS.map((s) => (
        <SectionCard key={s.key} number={s.number} title={s.title}>
          {s.render(data[s.key])}
        </SectionCard>
      ))}
    </div>
  );
};

export default FprFormView;
